import { ReactNode } from 'react';
import styles from './O_Header.module.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import A_Logo from '@/components/A_Atoms/A_Logo/A_Logo';
import { PATHS } from '@/constants/paths';
import A_HeaderItem from '@/components/A_Atoms/A_HeaderItem/A_HeaderItem';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import M_LanguageSwitcher from '@/components/M_Molecules/M_LanguageSwitcher/M_LanguageSwitcher';
import A_UserAvatar from '@/components/A_Atoms/A_UserAvatar/A_UserAvatar';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_SearchInput from '@/components/A_Atoms/A_SearchInput/A_SearchInput';
import Q_BlurEffect from '@/components/Q_Quarks/Q_BlurEffect/Q_BlurEffect';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUnreadChatCount } from '@/hooks/useChat';
import { useAuth } from '@/contexts/AuthContext';

interface O_HeaderProps {
  showCreateButton?: boolean;
  showSearchInput?: boolean;
  actionSlot?: ReactNode;
  hideRightNav?: boolean;
  onSearchChange?: (query: string) => void;
  onSearchSubmit?: (query: string) => void;
}

export default function O_Header({
  showCreateButton = true,
  showSearchInput = true,
  actionSlot,
  hideRightNav = false,
  onSearchChange,
  onSearchSubmit,
}: O_HeaderProps) {
  const { language } = useLanguage();
  const isMobile = useIsMobile(1160);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  // Только для авторизованных: на публичных страницах любой 401 сбрасывает
  // токены и уводит на регистрацию, а гостю там делать нечего.
  const unreadChatCount = useUnreadChatCount(isAuthenticated && !hideRightNav);

  return (
    <header className={styles.wrapper}>
      <Q_BlurEffect yAxis='top' />
      <div className={styles.side}>
        <Link to={PATHS.HOME} className={styles.logo_wrapper}>
          <A_Logo variant='withLabel' />
        </Link>
        <Link to={PATHS.HOME} className={styles.logo_mobile_wrapper}>
          <A_Logo variant='default' />
        </Link>
        <A_HeaderItem
          label={getTranslation(language, 'header.about')}
          to={PATHS.ABOUT}
          ariaLabel={getTranslation(language, 'header.about')}
        />
        <A_HeaderItem
          label={getTranslation(language, 'header.faq')}
          to={PATHS.FAQ}
          ariaLabel={getTranslation(language, 'header.faq')}
        />
        <M_LanguageSwitcher />
      </div>
      <div className={styles.side}>
        {showSearchInput && (
          <A_SearchInput
            placeholder={getTranslation(language, 'header.search')}
            onChange={onSearchChange}
            onSearch={onSearchSubmit}
          />
        )}
        {actionSlot && <div className={styles.action_slot}>{actionSlot}</div>}
        {showCreateButton &&
          (isMobile ? (
            <A_CircleButton
              variant='primary'
              icon={
                <Q_Icon.StarIcon
                  width={24}
                  height={24}
                  fill='white'
                  stroke='white'
                />
              }
              onClick={() => navigate(PATHS.CAPSULE_CREATE)}
            />
          ) : (
            <A_Button
              variant='primary'
              icon={
                <Q_Icon.StarIcon
                  width={24}
                  height={24}
                  fill='white'
                  stroke='white'
                />
              }
              fontSize='text-p-4'
              showIcon={true}
              size='small'
              onClick={() => navigate(PATHS.CAPSULE_CREATE)}
            >
              {getTranslation(language, 'header.create')}
            </A_Button>
          ))}
        {!hideRightNav && (
          <>
            <A_HeaderItem
              icon={<Q_Icon.ChatIcon width={24} height={24} fill='none' />}
              to={PATHS.CHAT}
              ariaLabel={getTranslation(language, 'header.chat')}
              active={location.pathname.startsWith(PATHS.CHAT)}
              badge={unreadChatCount}
              {...(!isMobile
                ? { label: getTranslation(language, 'header.chat') }
                : {})}
            />
            <A_HeaderItem
              icon={<Q_Icon.CapsIcon width={24} height={24} fill='none' />}
              to={PATHS.CAPSULES}
              ariaLabel={getTranslation(language, 'header.capsules')}
              {...(!isMobile
                ? { label: getTranslation(language, 'header.capsules') }
                : {})}
            />
            <A_HeaderItem
              icon={
                <Q_Icon.NotificationsIcon width={24} height={24} fill='none' />
              }
              to={PATHS.PROFILE_WITH_TAB('notifications')}
              ariaLabel={getTranslation(language, 'header.notifications')}
            />
            <A_UserAvatar isLink={true} />
          </>
        )}
      </div>
    </header>
  );
}
